<script setup lang="ts">
import { useVModel } from '@vueuse/core'

import { Biometrics, Form, Person } from '~/layers/forms/models'
import { maxValueValidator, minValueValidator, requiredValidator } from '~/utils/validators'

interface Props {
  modelValue: Biometrics
}

const props = defineProps<Props>()
const emits = defineEmits(['update:modelValue'])
const model = useVModel(props, 'modelValue', emits)

const { getBiometricsContraints } = useContractConstraints()
const { maxWeight, minWeight, maxHeight, minHeight } = getBiometricsContraints(model.value.gender)

const v$ = useValidation(
  () => ({
    weight: {
      required: requiredValidator,
      min: minValueValidator(computed(() => minWeight)),
      max: maxValueValidator(computed(() => maxWeight)),
    },
    height: { required: requiredValidator },
    gender: { required: requiredValidator },
    occupation: { required: requiredValidator },
  }),
  model,
  { $scope: Form.biometrics, $registerAs: Form.biometrics }
)
</script>

<template>
  <FormsGlobalContainer :identifier="Form.biometrics">
    <FormsGlobalTitle
      text="Información biométrica"
      icon-class="icon-[ph--fingerprint-bold]"
    />

    <InputField
      label="Peso (en libras)"
      class="md:col-span-2 lg:col-span-1"
      :error="v$.weight?.$error"
      :error-message="v$.weight?.$errors[0]?.$message.toString()"
    >
      <PvInputNumber v-model="model.weight" />
    </InputField>

    <InputField
      label="Altura (en pies ' y pulgadas '')"
      class="md:col-span-2 lg:col-span-1"
      :error="v$.height?.$error"
      :error-message="v$.height?.$errors[0]?.$message.toString()"
    >
      <PvSelect
        v-model="model.height"
        :options="Biometrics.heightOptions(maxHeight, minHeight)"
      />
    </InputField>

    <InputField
      label="Sexo biológico"
      class="md:col-span-2 lg:col-span-1"
      :error="v$.gender?.$error"
      :error-message="v$.gender?.$errors[0]?.$message.toString()"
    >
      <PvSelect
        v-model="model.gender"
        option-label="label"
        option-value="code"
        :options="Person.getGenderTypeList()"
      />
    </InputField>

    <InputField
      label="Ocupación"
      class="md:col-span-2 lg:col-span-1"
      :error="v$.occupation?.$error"
      :error-message="v$.occupation?.$errors[0]?.$message.toString()"
    >
      <PvSelect
        v-model="model.occupation"
        :options="[]"
      />
    </InputField>
  </FormsGlobalContainer>
</template>
